<!-- Loading State -->
@if (isLoading()) {
	<div class="flex items-center justify-center py-20">
		<div class="text-center">
			<div class="inline-block w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4"></div>
			<p class="text-charcoal/60">Loading services...</p>
		</div>
	</div>
}

<!-- Services Grid -->
@else if (services().length > 0) {
	<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
		@for (service of services(); track service.id) {
			<div class="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-charcoal/5 hover:border-primary/20">
				<!-- Service Image -->
				<div class="relative h-56 overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10">
					@if (service.image_path) {
						<img
							[src]="getImageUrl(service.image_path)"
							[alt]="service.service_name"
							class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
							(error)="onImageError($event)">
					} @else {
						<div class="w-full h-full flex items-center justify-center">
							<svg class="w-20 h-20 text-charcoal/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
							</svg>
						</div>
					}
					<div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

					<!-- Status Badge -->
					<div class="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm"
						[ngClass]="service.status ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'">
						{{ service.status ? 'Active' : 'Inactive' }}
					</div>
				</div>

				<!-- Service Details -->
				<div class="p-6">
					<h3 class="font-cenzo text-xl font-bold text-primary mb-2 line-clamp-1">{{ service.service_name }}</h3>
					<p class="text-charcoal/60 text-sm mb-4 line-clamp-2">{{ service.short_description || service.description }}</p>

					<!-- Pricing -->
					<div class="flex items-center gap-2 mb-4">
						<svg class="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
						</svg>
						<span class="text-lg font-bold text-primary">₹{{ service.pricing }}</span>
					</div>

					<!-- Created By -->
					@if (service.creator) {
						<div class="flex items-center gap-2 mb-4 text-xs text-charcoal/50">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
							</svg>
							<span>Created by {{ service.creator.first_name }} {{ service.creator.last_name }}</span>
						</div>
					}

					<!-- Actions -->
					<div class="flex gap-2 pt-4 border-t border-charcoal/10">
						<button
							(click)="editService(service)"
							class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg font-semibold transition-all duration-300">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
							</svg>
							Edit
						</button>
						<button
							(click)="viewService(service)"
							class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary/10 hover:bg-secondary/20 text-accent rounded-lg font-semibold transition-all duration-300">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
							</svg>
							View
						</button>
					</div>
				</div>
			</div>
		}
	</div>
} @else {
	<!-- Empty State -->
	<div class="flex items-center justify-center py-20">
		<div class="text-center max-w-md">
			<div class="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full flex items-center justify-center">
				<svg class="w-12 h-12 text-primary/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
				</svg>
			</div>
			<h3 class="text-xl font-cenzo font-bold text-primary mb-2">No Services Yet</h3>
			<p class="text-charcoal/60 mb-6">Get started by adding your first service to showcase your offerings.</p>
			<button
				(click)="openServiceModal()"
				class="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
				</svg>
				Add First Service
			</button>
		</div>
	</div>
}

<!-- View Service Modal Template -->
<ng-template #viewServiceModalTemplate>
	<div class="bg-white rounded-2xl shadow-2xl w-full animate-scale-in">
			@if (selectedService()) {
				<!-- Header with Image -->
				<div class="relative h-80">
					@if (selectedService()!.image_path) {
						<img
							[src]="getImageUrl(selectedService()!.image_path)"
							[alt]="selectedService()!.service_name"
							class="w-full h-full object-cover">
					} @else {
						<div class="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
							<svg class="w-24 h-24 text-charcoal/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
							</svg>
						</div>
					}
					<div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

					<!-- Close Button -->
					<button (click)="closeViewModal()" class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 flex items-center justify-center transition-colors">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
						</svg>
					</button>

					<!-- Status Badge -->
					<div class="absolute top-4 left-4 px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-md"
						[ngClass]="selectedService()!.status ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'">
						{{ selectedService()!.status ? 'Active' : 'Inactive' }}
					</div>

					<!-- Title -->
					<div class="absolute bottom-6 left-6 right-6">
						<h2 class="text-4xl font-cenzo font-bold text-white mb-2">{{ selectedService()!.service_name }}</h2>
						<p class="text-white/90 text-lg">{{ selectedService()!.short_description }}</p>
					</div>
				</div>

				<!-- Content -->
				<div class="p-8">
					<!-- Pricing -->
					<div class="flex items-center gap-3 mb-6 p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl">
						<svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
						</svg>
						<div>
							<p class="text-sm text-charcoal/60">Service Price</p>
							<p class="text-3xl font-bold text-primary">₹{{ selectedService()!.pricing }}</p>
						</div>
					</div>

				<!-- Description -->
				<div class="mb-6">
					<h3 class="text-lg font-cenzo font-bold text-primary mb-3">Description</h3>
					<div class="text-charcoal/80 leading-relaxed prose prose-sm max-w-none" [innerHTML]="selectedService()!.description"></div>
				</div>

					<!-- Creator Info -->
					@if (selectedService()?.creator) {
						<div class="flex items-center gap-3 p-4 bg-mist rounded-xl">
							<div class="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
								<span class="text-white font-bold">{{ selectedService()?.creator?.first_name?.charAt(0) }}{{ selectedService()?.creator?.last_name?.charAt(0) }}</span>
							</div>
							<div>
								<p class="text-sm text-charcoal/60">Created by</p>
								<p class="font-semibold text-charcoal">{{ selectedService()?.creator?.first_name }} {{ selectedService()?.creator?.last_name }}</p>
							</div>
						</div>
					}

					<!-- Actions -->
					<div class="flex gap-4 mt-8">
						<button
							(click)="editService(selectedService()!); closeViewModal()"
							class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl font-semibold transition-all">
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
							</svg>
							Edit Service
						</button>
						<button
							(click)="closeViewModal()"
							class="px-6 py-3 border-2 border-charcoal/20 text-charcoal rounded-xl font-semibold hover:bg-charcoal/5 transition-all">
							Close
						</button>
					</div>
				</div>
			}
	</div>
</ng-template>
